'use client';

import { motion } from 'framer-motion';
import { Search, Cpu, Globe, CheckCircle2 } from 'lucide-react';
import { EASE_CUSTOM } from '@/lib/animations'

const services = [
    {
        icon: Search,
        tag: 'Asesoría y diagnóstico',
        title: 'Entendemos tu negocio\nantes de tocar nada',
        desc: 'Analizamos tus procesos, herramientas y cuellos de botella reales. 40+ preguntas específicas de tu sector. El resultado: un informe con lo que hay que cambiar y por qué.',
        features: ['Diagnóstico sectorial profundo', 'Análisis de herramientas actuales', 'Informe con prioridades claras'],
        color: 'blue',
        size: 'large',
    },
    {
        icon: Cpu,
        tag: 'Implementación técnica',
        title: 'Lo que proponemos,\nlo construimos nosotros',
        desc: 'Sistemas a medida, automatizaciones, integraciones. No subcontratamos. El mismo equipo que diagnosticó tu negocio es el que implementa la solución.',
        features: ['Desarrollo 100% interno', 'Sprints con avance visible', 'Soporte post-lanzamiento'],
        color: 'indigo',
        size: 'small',
    },
    {
        icon: Globe,
        tag: 'Presencia digital',
        title: 'Si tu web también\nnecesita trabajo, lo vemos',
        desc: 'A veces el diagnóstico revela que el problema no es solo operativo: tu web no convierte, tu SEO no existe o tu imagen no transmite confianza. También lo resolvemos.',
        features: ['Webs de captación optimizadas', 'SEO desde la base', 'Velocidad y conversión'],
        color: 'violet',
        size: 'small',
    },
];

const colorMap = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-100', icon: 'text-blue-600', badge: 'text-blue-600 bg-blue-50 border-blue-100', check: 'text-blue-500' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-100', icon: 'text-indigo-600', badge: 'text-indigo-600 bg-indigo-50 border-indigo-100', check: 'text-indigo-500' },
    violet: { bg: 'bg-violet-50', border: 'border-violet-100', icon: 'text-violet-600', badge: 'text-violet-600 bg-violet-50 border-violet-100', check: 'text-violet-500' },
};

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i: number) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: EASE_CUSTOM } })
};

export default function Servicios() {
    return (
        <section id="servicios" className="py-20 md:py-32 relative z-10">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">

                <div className="mb-14 max-w-3xl">
                    <motion.div custom={0} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                        className="text-xs font-mono tracking-[0.3em] text-slate-400 uppercase mb-4">
                        Cómo ayudamos
                    </motion.div>
                    <motion.h2 custom={1} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                        className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
                        Asesoramos y ejecutamos.
                        <br />
                        <span className="text-slate-400 font-light">Todo bajo el mismo techo.</span>
                    </motion.h2>
                    <motion.p custom={2} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                        className="text-lg text-slate-500 font-light">
                        La mayoría de consultores te dan un PDF y se van. Las agencias construyen sin entender tu negocio. Nosotros hacemos las dos cosas.
                    </motion.p>
                </div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

                    {services.filter(s => s.size === 'large').map((s, i) => {
                        const c = colorMap[s.color as keyof typeof colorMap];
                        const Icon = s.icon;
                        return (
                            <motion.div key={s.tag} custom={i} variants={fadeUp} initial="hidden"
                                whileInView="show" viewport={{ once: true, margin: '-60px' }}
                                className="lg:col-span-7 glass-card rounded-3xl p-6 sm:p-8 lg:p-10 flex flex-col min-h-[300px] sm:min-h-[400px] group cursor-default">
                                <div className="flex items-start justify-between mb-6">
                                    <div className={`w-12 h-12 rounded-2xl ${c.bg} border ${c.border} flex items-center justify-center`}>
                                        <Icon className={`w-5 h-5 ${c.icon}`} />
                                    </div>
                                </div>
                                <span className={`text-xs font-mono border px-2.5 py-1 rounded-lg w-fit ${c.badge}`}>{s.tag}</span>
                                <h3 className="mt-4 text-3xl font-black tracking-tight text-slate-900 whitespace-pre-line leading-tight">{s.title}</h3>
                                <p className="mt-3 text-slate-500 font-light leading-relaxed">{s.desc}</p>
                                <ul className="mt-auto pt-6 border-t border-slate-200/60 flex flex-col gap-2.5">
                                    {s.features.map(f => (
                                        <li key={f} className="flex items-center gap-2.5 text-sm text-slate-700">
                                            <CheckCircle2 className={`w-4 h-4 shrink-0 ${c.check}`} />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        );
                    })}

                    <div className="lg:col-span-5 flex flex-col gap-4">
                        {services.filter(s => s.size === 'small').map((s, i) => {
                            const c = colorMap[s.color as keyof typeof colorMap];
                            const Icon = s.icon;
                            return (
                                <motion.div key={s.tag} custom={i + 1} variants={fadeUp} initial="hidden"
                                    whileInView="show" viewport={{ once: true, margin: '-60px' }}
                                    className="glass-card rounded-3xl p-7 flex flex-col group cursor-default flex-1">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={`w-10 h-10 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center`}>
                                            <Icon className={`w-4 h-4 ${c.icon}`} />
                                        </div>
                                    </div>
                                    <span className={`text-xs font-mono border px-2 py-0.5 rounded-md w-fit ${c.badge}`}>{s.tag}</span>
                                    <h3 className="mt-3 text-xl font-black tracking-tight text-slate-900 whitespace-pre-line leading-tight">{s.title}</h3>
                                    <p className="mt-2 text-sm text-slate-500 font-light leading-relaxed">{s.desc}</p>
                                    <ul className="mt-4 pt-4 border-t border-slate-200/60 flex flex-col gap-2">
                                        {s.features.map(f => (
                                            <li key={f} className="flex items-center gap-2 text-xs text-slate-600">
                                                <CheckCircle2 className={`w-3.5 h-3.5 shrink-0 ${c.check}`} />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.3 }}
                    className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <a href="#contacto"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-all active:scale-95">
                        ¿No sabes por dónde empezar? Te orientamos →
                    </a>
                    <span className="text-xs text-slate-400 font-mono uppercase tracking-widest">
                        Diagnóstico gratuito · Sin compromiso
                    </span>
                </motion.div>
            </div>
        </section>
    );
}
